import React from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Download, BookOpen, Eye, Zap, Share2, Check } from "lucide-react";
import Navbar from "../components/storyforge/Navbar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { imageUrl, api } from "../lib/api";
import { SF } from "../constants/testIds";
import { toast } from "sonner";
import { useOrderPolling } from "../hooks/useOrderPolling";
import { usePaymentVerification } from "../hooks/usePaymentVerification";
import { useDocumentMeta } from "../hooks/useDocumentMeta";
import { STAGES, statusToIndex, isOrderComplete, isShippingVisible } from "./orderStages";
import { OrderProgressTimeline } from "./order/OrderProgressTimeline";
import { ShippingCard } from "./order/ShippingCard";
import { LiveEventTrail } from "./order/LiveEventTrail";

export default function OrderView() {
    const { orderId } = useParams();
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const awaitingCheckoutUrl = params.get("checkout_url");

    usePaymentVerification(sessionId);
    const { order, setOrder } = useOrderPolling(orderId);

    useDocumentMeta({
        title: order ? `${order.child_name}'s book — Athena's Atlas` : "Your order — Athena's Atlas",
        description: "Track your personalized children's book in real time — from story-writing to illustration to printing and shipping.",
    });

    if (!order) return <OrderLoadingState />;

    const stageIdx = statusToIndex(order.status);
    const complete = isOrderComplete(order.status);
    const failed = order.status === "failed";

    return (
        <div className="min-h-screen paper-bg">
            <Navbar />
            <div className="max-w-5xl mx-auto px-6 md:px-10 pt-12 pb-24">
                <OrderHeader order={order} stageIdx={stageIdx} />

                {awaitingCheckoutUrl && !order.paid && (
                    <ResumePaymentBanner checkoutUrl={awaitingCheckoutUrl} />
                )}

                {!complete && !failed && <OrderProgressTimeline order={order} />}
                {!complete && !failed && <NudgeButton order={order} onNudged={setOrder} />}
                <LiveEventTrail order={order} />
                <CoverReveal order={order} />
                {!complete && (order.pdf_url || order.cover_pdf_url) && (
                    <div
                        data-testid="sf-order-preview-card"
                        className="paper-card rounded-3xl p-6 md:p-8 mb-12 flex flex-wrap items-center justify-between gap-6"
                    >
                        <div className="min-w-0">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Print-ready files</p>
                            <h3 className="font-serif text-xl text-primary">Peek at what&apos;s being printed</h3>
                            <p className="text-sm text-muted-foreground mt-1">
                                The cover and interior PDFs are locked in — Lulu is finalizing your print job.
                            </p>
                        </div>
                        <PreviewButtons order={order} />
                    </div>
                )}
                {failed && <FailedBanner order={order} />}
                {complete && <CompleteBanner order={order} />}
                {isShippingVisible(order) && (
                    <ShippingCard order={order} orderId={orderId} onRefreshed={setOrder} />
                )}

                <CharacterReveal order={order} />
                <PagesGrid pages={order.pages} />

                <div className="mt-16 text-center">
                    <Link to="/dashboard">
                        <Button variant="outline" className="rounded-xl">
                            <BookOpen className="w-4 h-4 mr-2" />All my books
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
}

function OrderLoadingState() {
    return (
        <div className="min-h-screen paper-bg">
            <Navbar />
            <div className="max-w-4xl mx-auto px-6 pt-24 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
                <p className="mt-4 text-muted-foreground font-serif italic">Opening your book...</p>
            </div>
        </div>
    );
}

/**
 * Live cover reveal — fades the cover illustration in the moment the backend
 * publishes `cover_image_url` (i.e. as soon as the OpenAI cover render lands),
 * BEFORE the interior PDF has finished stitching. This gives parents the
 * emotional "there's my kid's book!" moment while the pipeline keeps working.
 *
 * Rendered on any status that has cover_image_url, but hidden once we've moved
 * past `sent_to_print` (at which point the ShippingCard is the hero card).
 */
function CoverReveal({ order }) {
    // Prefer the baked front-cover PNG (illustration + title + subtitle
    // rendered together). Fall back to the raw square illustration for older
    // orders that pre-date the front-cover mockup step.
    const url = order?.cover_front_image_url || order?.cover_image_url;
    if (!url) return null;
    if (order.status === "sent_to_print" || order.status === "shipped") return null;
    const bookTitle = order.book_title || `${order.child_name} and the ${order.theme}`;
    // If we're showing the baked mockup, the title/subtitle are already IN the
    // image — no CSS overlay needed. If we're falling back to the raw square,
    // we still overlay title/subtitle so the reveal looks like a book cover.
    const showTextOverlay = !order?.cover_front_image_url;
    return (
        <div
            data-testid="sf-order-cover-reveal"
            className="mb-12 relative overflow-hidden rounded-3xl paper-card p-6 md:p-10 animate-in fade-in duration-1000"
        >
            <div className="grid md:grid-cols-[1fr_1.4fr] gap-8 items-center">
                <div className="relative aspect-square rounded-2xl overflow-hidden shadow-2xl bg-muted">
                    <img
                        data-testid="sf-order-cover-reveal-image"
                        src={imageUrl(url)}
                        alt={`Cover art for ${bookTitle}`}
                        className="absolute inset-0 w-full h-full object-cover animate-in zoom-in-95 duration-1000"
                    />
                    {showTextOverlay && (
                        <div className="absolute inset-0 flex flex-col justify-start items-center pt-6 md:pt-10 px-4 text-center pointer-events-none">
                            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/40" />
                            <h3
                                data-testid="sf-order-cover-reveal-title"
                                className="relative font-serif text-white text-2xl md:text-3xl leading-tight [text-shadow:_0_2px_8px_rgb(0_0_0_/_60%)] max-w-[85%]"
                            >
                                {bookTitle}
                            </h3>
                            <p className="relative mt-2 font-serif italic text-white/90 text-base md:text-lg [text-shadow:_0_2px_6px_rgb(0_0_0_/_60%)]">
                                {order.child_name}
                            </p>
                        </div>
                    )}
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Cover reveal</p>
                    <h2 className="font-serif text-3xl md:text-4xl text-primary tracking-tight mb-3">
                        Here&apos;s {order.child_name}&apos;s cover.
                    </h2>
                    <p className="text-muted-foreground leading-relaxed mb-5">
                        The interior is being bound now — 24 illustrated pages, embedded fonts,
                        print-ready. We&apos;ll ship the hardcover to your address the moment Lulu accepts it.
                    </p>
                    <ShareCoverButton order={order} />
                </div>
            </div>
        </div>
    );
}

/**
 * "Share this cover" — uses the native Web Share API on mobile / secure browsers,
 * falls back to copying the URL to clipboard on desktop. We append `?ref=cover`
 * so we can attribute any resulting signup back to the reveal moment. When the
 * OS share sheet is available we also attach the cover PNG as a File so the
 * share previews the actual illustration instead of just a link.
 */
function ShareCoverButton({ order }) {
    const [copied, setCopied] = React.useState(false);
    const bookTitle = order.book_title || `${order.child_name} and the ${order.theme}`;
    // Share URL points to the backend OG-unfurl endpoint. Human clicks get
    // meta-refreshed straight into the app; crawlers see rich meta tags for
    // Facebook / Twitter / iMessage / WhatsApp / LinkedIn previews.
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const shareUrl = `${backend}/api/share/order/${order.id}`;
    const shareText = `Look at ${order.child_name}'s new storybook — "${bookTitle}". Made on Athena's Atlas.`;

    const onShare = async () => {
        // Prefer the baked front-cover PNG (has title/subtitle rendered in)
        // so the OS share sheet previews a real book cover, not a raw square
        // illustration.
        const shareImgUrl = order.cover_front_image_url || order.cover_image_url;
        // Try to include the cover image as a file so the share preview is rich.
        let files;
        try {
            const resp = await fetch(imageUrl(shareImgUrl));
            const blob = await resp.blob();
            files = [new File([blob], `${order.child_name}-cover.png`, { type: blob.type || "image/png" })];
        } catch (e) {
            files = undefined;
        }
        const shareData = { title: bookTitle, text: shareText, url: shareUrl };
        if (files && navigator.canShare && navigator.canShare({ files })) {
            shareData.files = files;
        }
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                if (e?.name === "AbortError") return; // user cancelled
                // fall through to clipboard
            }
        }
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link copied to clipboard");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Couldn't copy link — please copy manually from the URL bar.");
        }
    };

    return (
        <Button
            data-testid={SF.orderShareCover}
            onClick={onShare}
            variant="outline"
            className="rounded-xl"
        >
            {copied ? (
                <><Check className="w-4 h-4 mr-2" /> Link copied</>
            ) : (
                <><Share2 className="w-4 h-4 mr-2" /> Share this cover</>
            )}
        </Button>
    );
}

/**
 * NudgeButton — shows a subtle "Nudge printer" button on any in-progress order
 * whose `updated_at` is more than 3 min stale. Calls POST /api/orders/{id}/resume
 * which is idempotent: it re-runs whichever pipeline stage is stuck (cover art,
 * PDF assembly, Lulu submission…) and never regenerates pages that already
 * finished. Provides an obvious self-service escape hatch instead of a support
 * ticket every time an OpenAI IPM hiccup strands an order.
 */
function NudgeButton({ order, onNudged }) {
    const [nudging, setNudging] = React.useState(false);
    // How stale is this order? Use updated_at if present, fall back to created_at.
    const ts = order?.updated_at || order?.created_at;
    const staleMs = ts ? Date.now() - new Date(ts).getTime() : 0;
    // Only surface the nudge after 3 min of no status movement so parents don't
    // hammer it during normal pipeline pacing (which naturally sits at each
    // stage for 5–90 s at a time).
    const isStuck = staleMs > 3 * 60 * 1000;
    if (!isStuck) return null;

    const staleMin = Math.floor(staleMs / 60000);
    const onNudge = async () => {
        setNudging(true);
        try {
            await api.post(`/orders/${order.id}/resume`);
            toast.success("Poking the pipeline — the page will refresh once it moves.");
            // Trigger an immediate refetch so the UI reflects any status flip
            if (onNudged) {
                const { data: fresh } = await api.get(`/orders/${order.id}`);
                onNudged(fresh);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Nudge failed — please try again in a moment.");
        } finally {
            setNudging(false);
        }
    };

    return (
        <div
            data-testid="sf-order-nudge-card"
            className="mb-12 rounded-2xl bg-accent/20 border border-accent/40 p-5 md:p-6 flex flex-wrap items-center justify-between gap-4"
        >
            <div className="min-w-0 flex-1">
                <p className="font-serif text-lg text-primary">
                    Been a minute? Give it a nudge.
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                    No status change for {staleMin} min. Sometimes the image or print API
                    needs a poke — this restarts the current step without redoing what&apos;s already done.
                </p>
            </div>
            <Button
                data-testid={SF.orderNudge}
                onClick={onNudge}
                disabled={nudging}
                className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
            >
                {nudging ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Nudging…</>
                ) : (
                    <><Zap className="w-4 h-4 mr-2" /> Nudge printer</>
                )}
            </Button>
        </div>
    );
}

function OrderHeader({ order, stageIdx }) {
    const bookTitle = order.book_title || `${order.child_name} and the ${order.theme}`;
    return (
        <>
            <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-4">
                {order.child_name}&apos;s storybook
            </p>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                <h1 className="font-serif text-4xl md:text-5xl text-primary tracking-tight">
                    {bookTitle}
                </h1>
                <Badge data-testid={SF.orderStatus} variant="outline" className="text-sm py-1.5 px-3 border-primary/30 bg-muted/40">
                    {STAGES[stageIdx]?.label || order.status}
                </Badge>
            </div>
        </>
    );
}

function ResumePaymentBanner({ checkoutUrl }) {
    return (
        <div className="paper-card rounded-3xl p-6 md:p-8 mb-8 flex flex-wrap items-center justify-between gap-4 border-accent/40 bg-accent/5">
            <div>
                <h2 className="font-serif text-2xl text-primary">Finish your payment</h2>
                <p className="text-muted-foreground mt-1">If the Stripe tab didn&apos;t open or you closed it, use the button to resume checkout.</p>
            </div>
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" data-testid="sf-order-resume-checkout">
                <Button className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90">
                    Resume payment — $45
                </Button>
            </a>
        </div>
    );
}

function FailedBanner({ order }) {
    const err = order?.error;
    return (
        <div className="paper-card rounded-3xl p-8 border-destructive/40 mb-12">
            <h2 className="font-serif text-2xl text-destructive">Something went wrong</h2>
            <p className="text-muted-foreground mt-2">
                We had trouble generating this story. Our team has been notified — your card was not charged again.
            </p>
            {err && (
                <p
                    data-testid="order-error-detail"
                    className="mt-3 text-xs font-mono text-muted-foreground/80 bg-destructive/5 rounded-lg p-3 break-words"
                >
                    Details: {err}
                </p>
            )}
            <p className="text-sm text-muted-foreground mt-4">
                If this doesn&apos;t clear on its own,{" "}
                <a href="mailto:support@athenasatlas.com" className="underline">email support</a>{" "}
                with your order number and we&apos;ll get it running.
            </p>
        </div>
    );
}

function CompleteBanner({ order }) {
    return (
        <div className="paper-card rounded-3xl p-6 md:p-8 mb-12 flex flex-wrap items-center justify-between gap-6">
            <div>
                <h2 className="font-serif text-2xl text-primary">Your book is ready.</h2>
                <p className="text-muted-foreground mt-1">
                    {order.lulu_order_id ? (
                        <>Lulu print order <span data-testid={SF.orderLuluId} className="font-mono text-foreground">{order.lulu_order_id}</span></>
                    ) : "Final printing started."}
                </p>
            </div>
            <PreviewButtons order={order} />
        </div>
    );
}

/**
 * Preview buttons: open the print-ready cover + interior PDFs in a new tab.
 * Rendered on the CompleteBanner (once the book ships to Lulu) and also on
 * the timeline card the moment PDFs are written (status ≥ complete) so a
 * parent can peek at what's actually being printed while Lulu is processing.
 */
function PreviewButtons({ order, compact = false }) {
    const hasCover = Boolean(order.cover_pdf_url);
    const hasInterior = Boolean(order.pdf_url);
    if (!hasCover && !hasInterior) return null;
    return (
        <div className={`flex flex-wrap gap-3 ${compact ? "" : "justify-end"}`}>
            {hasCover && (
                <a
                    href={imageUrl(order.cover_pdf_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="sf-order-preview-cover"
                >
                    <Button variant="outline" className="rounded-xl">
                        <Eye className="w-4 h-4 mr-2" /> Preview cover
                    </Button>
                </a>
            )}
            {hasInterior && (
                <a
                    href={imageUrl(order.pdf_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={SF.orderPdfDownload}
                >
                    <Button className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90">
                        <Download className="w-4 h-4 mr-2" /> Preview interior
                    </Button>
                </a>
            )}
        </div>
    );
}

function CharacterReveal({ order }) {
    if (!order.character_sheet && !order.child_photo_url) return null;
    const hasPhoto = Boolean(order.child_photo_url);
    return (
        <div className="grid md:grid-cols-12 gap-6 mb-12">
            {hasPhoto && (
                <div className="md:col-span-4 paper-card rounded-3xl overflow-hidden aspect-square">
                    <img
                        src={imageUrl(order.child_photo_url)}
                        alt="child reference"
                        data-testid="sf-order-child-photo"
                        className="w-full h-full object-cover"
                    />
                </div>
            )}
            {order.character_sheet && (
                <div
                    data-testid={SF.orderCharacterSheet}
                    className={`paper-card rounded-3xl p-6 md:p-8 ${hasPhoto ? "md:col-span-8" : "md:col-span-12"}`}
                >
                    <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-3">Character sheet</p>
                    <p className="font-serif italic text-lg text-foreground leading-relaxed">{order.character_sheet}</p>
                    {order.child_likeness && hasPhoto && (
                        <p className="mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                            <span className="uppercase tracking-wider text-xs">From photo · </span>
                            {order.child_likeness}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

function PagesGrid({ pages }) {
    if (!pages?.length) return null;
    return (
        <div data-testid={SF.orderPagesGrid} className="space-y-10">
            <h2 className="font-serif text-3xl text-primary">The book</h2>
            <AnimatePresence>
                {pages.map((p, i) => <PageCard key={p.id} page={p} index={i} />)}
            </AnimatePresence>
        </div>
    );
}

function PageCard({ page, index }) {
    return (
        <motion.article
            data-testid={SF.orderPage}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: Math.min(index * 0.04, 0.5) }}
            className="paper-card rounded-3xl overflow-hidden grid md:grid-cols-5 gap-0"
        >
            <div className="md:col-span-3 bg-muted/30 aspect-square md:aspect-auto flex items-center justify-center">
                {page.image_url ? (
                    <img src={imageUrl(page.image_url)} alt={`page ${page.page_number}`} className="w-full h-full object-cover" />
                ) : (
                    <div className="text-muted-foreground text-sm flex flex-col items-center p-10">
                        <Loader2 className="w-6 h-6 animate-spin mb-3" />
                        {page.image_status === "failed" ? "Illustration retry pending" : "Illustrating..."}
                    </div>
                )}
            </div>
            <div className="md:col-span-2 p-6 md:p-10 flex flex-col justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Page {page.page_number}</p>
                    <p className="font-serif text-lg md:text-xl text-foreground leading-relaxed">{page.story_text}</p>
                </div>
            </div>
        </motion.article>
    );
}
